import { Star } from "lucide-react";
import { TabsContent } from "@/components/ui/tabs";
import { bandMeta } from "@/lib/api";
import { BarList, RatingList } from "@/components/admin/widgets";

export const AnalyticsPanel = ({ analytics }) => {
  const maxCourse = Math.max(1, ...(analytics?.by_course || []).map((x) => x.count));
  const maxCountry = Math.max(1, ...(analytics?.by_country || []).map((x) => x.count));
  const maxBand = Math.max(1, ...(analytics?.by_band || []).map((x) => x.count));
  const maxDay = Math.max(1, ...(analytics?.over_time || []).map((x) => x.count));
  const maxSource = Math.max(1, ...(analytics?.visitors?.by_source || []).map((x) => x.count));
  const maxDevice = Math.max(1, ...(analytics?.visitors?.by_device || []).map((x) => x.count));
  const maxRating = Math.max(1, ...(analytics?.ratings?.distribution || []).map((x) => x.count));

  return (
    <TabsContent value="analytics" className="mt-6 space-y-6" data-testid="analytics-panel">
      <div className="grid gap-4 sm:grid-cols-4">
        <div className="bg-white border border-border rounded-2xl p-6">
          <div className="text-4xl font-heading text-ink">{analytics?.visitors?.unique_visitors ?? 0}</div>
          <div className="text-sm text-ink-muted">Unique visitors</div>
        </div>
        <div className="bg-white border border-border rounded-2xl p-6">
          <div className="text-4xl font-heading text-ink">{analytics?.visitors?.total_pageviews ?? 0}</div>
          <div className="text-sm text-ink-muted">Page views</div>
        </div>
        <div className="bg-white border border-border rounded-2xl p-6">
          <div className="text-4xl font-heading text-ink">{analytics?.total ?? 0}</div>
          <div className="text-sm text-ink-muted">Submissions</div>
        </div>
        <div className="bg-white border border-border rounded-2xl p-6">
          <div className="text-4xl font-heading text-ink">{analytics?.visitors?.submitted_visitors ?? 0}</div>
          <div className="text-sm text-ink-muted">Visitors who submitted</div>
        </div>
      </div>

      {/* Sales & overall conversion */}
      <div className="grid gap-4 sm:grid-cols-4" data-testid="overall-totals">
        <div className="bg-white border border-border rounded-2xl p-6">
          <div className="text-4xl font-heading text-ink">{analytics?.totals?.total_visits ?? 0}</div>
          <div className="text-sm text-ink-muted">Total visits</div>
        </div>
        <div className="bg-white border border-border rounded-2xl p-6">
          <div className="text-4xl font-heading text-ink">{analytics?.totals?.total_submissions ?? 0}</div>
          <div className="text-sm text-ink-muted">Submissions</div>
        </div>
        <div className="bg-white border border-border rounded-2xl p-6">
          <div className="text-4xl font-heading text-forest">{analytics?.totals?.total_sales ?? 0}</div>
          <div className="text-sm text-ink-muted">Completed sales</div>
        </div>
        <div className="bg-white border border-border rounded-2xl p-6">
          <div className="text-4xl font-heading text-clay">{analytics?.totals?.conversion_visit_to_sale ?? 0}%</div>
          <div className="text-sm text-ink-muted">Visit → sale ({analytics?.totals?.conversion_submission_to_sale ?? 0}% of submissions)</div>
        </div>
      </div>

      {/* Guides → purchase funnel */}
      <div className="bg-white border border-border rounded-2xl p-6" data-testid="funnel">
        <h3 className="font-heading text-lg text-ink mb-4">Guides → purchase funnel</h3>
        {(() => {
          const stages = analytics?.funnel?.stages || [];
          const top = Math.max(1, stages[0]?.count || 0);
          return (
            <div className="space-y-3">
              {stages.map((s, i) => (
                <div key={s.name} className="flex items-center gap-3">
                  <div className="w-44 text-sm text-ink-soft">{s.name}</div>
                  <div className="flex-1 h-6 rounded-lg bg-sand-deep overflow-hidden">
                    <div className="h-full rounded-lg bg-clay/80 transition-all" style={{ width: `${((s.count || 0) / top) * 100}%` }} />
                  </div>
                  <div className="w-16 text-right text-sm font-medium text-ink">{s.count || 0}</div>
                  <div className="w-14 text-right text-xs text-ink-faint">{i === 0 ? "" : `${s.rate ?? 0}%`}</div>
                </div>
              ))}
            </div>
          );
        })()}
        <p className="mt-3 text-xs text-ink-faint">Conversion shown is from the previous stage. Visits that don't come via a guide count as direct and aren't shown here.</p>
      </div>

      {/* Per-guide performance */}
      <div className="bg-white border border-border rounded-2xl p-6" data-testid="guide-conversions">
        <h3 className="font-heading text-lg text-ink mb-4">Which guides convert best</h3>
        {(analytics?.funnel?.per_guide || []).length === 0 ? (
          <p className="text-sm text-ink-muted">No guide traffic yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-ink-faint border-b border-border">
                  <th className="py-2 pr-3 font-medium">Guide</th>
                  <th className="py-2 px-2 font-medium text-right">Views</th>
                  <th className="py-2 px-2 font-medium text-right">Clicks</th>
                  <th className="py-2 px-2 font-medium text-right">Submissions</th>
                  <th className="py-2 px-2 font-medium text-right">Sales</th>
                  <th className="py-2 pl-2 font-medium text-right">View→sale</th>
                </tr>
              </thead>
              <tbody>
                {analytics.funnel.per_guide.map((g) => (
                  <tr key={g.slug} className="border-b border-border/60" data-testid={`guide-conv-${g.slug}`}>
                    <td className="py-2 pr-3 text-ink truncate max-w-[220px]">{g.title}</td>
                    <td className="py-2 px-2 text-right text-ink-soft">{g.views}</td>
                    <td className="py-2 px-2 text-right text-ink-soft">{g.clickthroughs}</td>
                    <td className="py-2 px-2 text-right text-ink-soft">{g.submissions}</td>
                    <td className="py-2 px-2 text-right text-forest font-medium">{g.sales}</td>
                    <td className="py-2 pl-2 text-right text-clay">{g.view_to_sale}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Would you recommend */}
      <div className="grid gap-4 sm:grid-cols-3" data-testid="recommend-summary">
        <div className="bg-white border border-border rounded-2xl p-6">
          <div className="text-4xl font-heading text-forest">{analytics?.recommend?.rate ?? 0}%</div>
          <div className="text-sm text-ink-muted">Would recommend</div>
        </div>
        <div className="bg-white border border-border rounded-2xl p-6">
          <div className="text-4xl font-heading text-ink">{analytics?.recommend?.up ?? 0}</div>
          <div className="text-sm text-ink-muted">Recommended</div>
        </div>
        <div className="bg-white border border-border rounded-2xl p-6">
          <div className="text-4xl font-heading text-ink">{analytics?.recommend?.down ?? 0}</div>
          <div className="text-sm text-ink-muted">Did not recommend</div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="bg-white border border-border rounded-2xl p-6">
          <h3 className="font-heading text-lg text-ink mb-4">Visitors by source</h3>
          <BarList items={analytics?.visitors?.by_source || []} max={maxSource} />
        </div>
        <div className="bg-white border border-border rounded-2xl p-6">
          <h3 className="font-heading text-lg text-ink mb-4">Visitors by device</h3>
          <BarList items={analytics?.visitors?.by_device || []} max={maxDevice} />
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="bg-white border border-border rounded-2xl p-6">
          <h3 className="font-heading text-lg text-ink mb-4">By overall band</h3>
          <div className="space-y-2">
            {(analytics?.by_band || []).map((b) => (
              <div key={b.name} className="flex items-center gap-3">
                <div className="w-28 text-sm" style={{ color: bandMeta(b.name).color }}>{b.name}</div>
                <div className="flex-1 h-3 rounded-full bg-sand-deep overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${(b.count / maxBand) * 100}%`, backgroundColor: bandMeta(b.name).color }} />
                </div>
                <div className="w-8 text-right text-sm font-medium text-ink">{b.count}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-border rounded-2xl p-6">
          <h3 className="font-heading text-lg text-ink mb-4">Submissions over time</h3>
          {(analytics?.over_time || []).length === 0 ? (
            <p className="text-sm text-ink-muted">No data yet.</p>
          ) : (
            <div className="flex items-end gap-1 h-32">
              {analytics.over_time.map((d) => (
                <div key={d.date} className="flex-1 flex flex-col items-center justify-end" title={`${d.date}: ${d.count}`}>
                  <div className="w-full rounded-t bg-clay" style={{ height: `${(d.count / maxDay) * 100}%` }} />
                  <div className="mt-1 text-[9px] text-ink-faint truncate w-full text-center">{d.date.slice(5)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-border rounded-2xl p-6">
          <h3 className="font-heading text-lg text-ink mb-4">By course / subject</h3>
          <BarList items={analytics?.by_course || []} max={maxCourse} />
        </div>

        <div className="bg-white border border-border rounded-2xl p-6">
          <h3 className="font-heading text-lg text-ink mb-4">By country</h3>
          <BarList items={analytics?.by_country || []} max={maxCountry} />
        </div>
      </div>

      {/* Feedback ratings */}
      <div className="grid gap-4 sm:grid-cols-3" data-testid="ratings-summary">
        <div className="bg-white border border-border rounded-2xl p-6">
          <div className="flex items-center gap-2">
            <Star className="h-6 w-6 fill-amber-400 text-amber-400" />
            <span className="text-4xl font-heading text-ink">{analytics?.ratings?.average ?? 0}</span>
          </div>
          <div className="text-sm text-ink-muted mt-1">Average feedback rating</div>
        </div>
        <div className="bg-white border border-border rounded-2xl p-6">
          <div className="text-4xl font-heading text-ink">{analytics?.ratings?.count ?? 0}</div>
          <div className="text-sm text-ink-muted">Ratings received</div>
        </div>
        <div className="bg-white border border-border rounded-2xl p-6">
          <h3 className="text-sm font-medium text-ink mb-2">Distribution</h3>
          <div className="space-y-1">
            {(analytics?.ratings?.distribution || []).map((d) => (
              <div key={d.stars} className="flex items-center gap-2 text-xs">
                <span className="w-8 text-ink-muted">{d.stars}★</span>
                <div className="flex-1 h-2 rounded-full bg-sand-deep overflow-hidden">
                  <div className="h-full bg-amber-400" style={{ width: `${maxRating ? (d.count / maxRating) * 100 : 0}%` }} />
                </div>
                <span className="w-6 text-right text-ink">{d.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="bg-white border border-border rounded-2xl p-6">
          <h3 className="font-heading text-lg text-ink mb-4">Avg rating by course / subject</h3>
          <RatingList items={analytics?.ratings?.by_course || []} />
        </div>
        <div className="bg-white border border-border rounded-2xl p-6">
          <h3 className="font-heading text-lg text-ink mb-4">Avg rating by country</h3>
          <RatingList items={analytics?.ratings?.by_country || []} />
        </div>
      </div>

      <p className="text-xs text-ink-faint">
        Only non-identifying data is stored: course, country, the bands awarded, the feedback ratings given, and a timestamp. Statements and personal details are never saved.
      </p>
    </TabsContent>
  );
};
